import { z } from "zod";
import { MAXIMUM_ATTRIBUTION_MATURITY_DAYS, MAXIMUM_REPORT_DURATION_SECONDS, MAXIMUM_REPORT_ROWS, MAXIMUM_REPORTING_RANGE_DAYS, MAXIMUM_TREND_POINTS, MAXIMUM_TREND_SERIES, } from "./reporting-constants.js";
import { reportPlayStatusSchema } from "./report-filters.js";
import { MAXIMUM_VIDEO_SELECTIONS, videoBrandIdSchema, videoIdSchema, } from "./video.js";
const finite = z.number().finite();
const nullableFinite = finite.nullable();
const coverageRate = finite.min(0).max(1);
const nullableCoverageRate = coverageRate.nullable();
export const publicReportDateSchema = z
    .string()
    .regex(/^(?!0000-)\d{4}-\d{2}-\d{2}$/)
    .refine((value) => {
    const parsed = new Date(`${value}T00:00:00.000Z`);
    return (!Number.isNaN(parsed.valueOf()) &&
        parsed.toISOString().slice(0, 10) === value);
}, "Expected a real ISO date");
export const publicThumbnailSchema = z
    .string()
    .max(8_192)
    .refine((value) => {
    try {
        return new URL(value).protocol === "https:";
    }
    catch {
        return false;
    }
}, "Expected an HTTPS thumbnail URL")
    .nullable();
export const reportMetricShape = {
    addToCart: finite,
    aov: nullableFinite,
    clicks: finite,
    cpa: nullableFinite,
    cpc: nullableFinite,
    cpm: nullableFinite,
    ctr: nullableFinite,
    cvr: nullableFinite,
    holdRate: nullableFinite,
    hookRate: nullableFinite,
    impressions: finite,
    outboundClicks: finite,
    outboundCtr: nullableFinite,
    purchases: finite,
    revenue: finite,
    roas: nullableFinite,
    spend: finite,
    uniqueClicks: finite,
};
export const videoIdentitySchema = z
    .object({
    adCount: z.number().int().nonnegative().max(1_000_000),
    brandId: videoBrandIdSchema,
    displayAdName: z.string().min(1).max(10_000),
    lengthSeconds: finite
        .nonnegative()
        .max(MAXIMUM_REPORT_DURATION_SECONDS)
        .nullable(),
    thumbnailUrl: publicThumbnailSchema,
    videoId: videoIdSchema,
})
    .strict();
export const videoMetricRowSchema = videoIdentitySchema
    .extend({
    ...reportMetricShape,
    currency: z.string().min(1).max(32),
    firstSeen: publicReportDateSchema,
    playStatus: reportPlayStatusSchema,
    videoCoverageRate: nullableCoverageRate,
})
    .strict();
export const reportTotalSchema = z
    .object({
    ...reportMetricShape,
    currency: z.string().min(1).max(32),
    unknownDurationVideoCount: z.number().int().nonnegative(),
    videoCount: z.number().int().nonnegative(),
    videoCoverageRate: nullableCoverageRate,
})
    .strict();
export const reconciliationDaySchema = z
    .object({
    currency: z.string().min(1).max(32),
    day: publicReportDateSchema,
    nonVideoSpend: finite,
    resolutionRate: nullableCoverageRate,
    totalSpend: finite,
    unresolvedSpend: finite,
    videoSpend: finite,
})
    .strict();
export const reconciliationSchema = z
    .object({
    currency: z.string().min(1).max(32),
    days: z.array(reconciliationDaySchema).max(MAXIMUM_REPORTING_RANGE_DAYS),
    nonVideoSpend: finite,
    resolutionRate: nullableCoverageRate,
    totalSpend: finite,
    unresolvedSpend: finite,
    videoSpend: finite,
})
    .strict();
export const reportQueryRowSchema = z
    .object({
    reconciliation: reconciliationSchema,
    rows: z.array(videoMetricRowSchema).max(MAXIMUM_REPORT_ROWS),
    total: reportTotalSchema,
})
    .strict();
export const drilldownAdRowSchema = z
    .object({
    ...reportMetricShape,
    adId: z.string().min(1).max(255),
    adName: z.string().min(1).max(10_000),
    adsetName: z.string().max(10_000).nullable(),
    campaignName: z.string().max(10_000).nullable(),
    currency: z.string().min(1).max(32),
    thumbnailUrl: publicThumbnailSchema,
    videoCoverageRate: nullableCoverageRate,
})
    .strict();
export const drilldownQueryRowSchema = z
    .object({
    rows: z.array(drilldownAdRowSchema).max(100_000),
    total: reportTotalSchema.omit({
        unknownDurationVideoCount: true,
        videoCount: true,
    }),
})
    .strict();
export const trendPointSchema = z
    .object({
    brandId: videoBrandIdSchema.nullable(),
    day: publicReportDateSchema,
    maturing: z.boolean(),
    value: nullableFinite,
    videoId: videoIdSchema.nullable(),
})
    .strict()
    .refine(({ brandId, videoId }) => (brandId === null) === (videoId === null), "Trend point identity must be wholly present or absent");
export const trendSeriesSchema = videoIdentitySchema;
export const trendResolutionDaySchema = z
    .object({
    belowThreshold: z.boolean(),
    day: publicReportDateSchema,
    rate: nullableCoverageRate,
})
    .strict();
export const trendQueryRowSchema = z
    .object({
    currency: z.string().min(1).max(32),
    points: z.array(trendPointSchema).max(MAXIMUM_TREND_POINTS),
    resolution: z
        .array(trendResolutionDaySchema)
        .max(MAXIMUM_REPORTING_RANGE_DAYS),
    series: z.array(trendSeriesSchema).max(MAXIMUM_TREND_SERIES),
    total: nullableFinite,
})
    .strict();
export const comparisonQueryRowSchema = z
    .object({
    rows: z.array(videoMetricRowSchema).max(MAXIMUM_VIDEO_SELECTIONS),
})
    .strict();
export const exactVideoDetailQueryRowSchema = z
    .object({ row: videoMetricRowSchema.nullable() })
    .strict();
function validateExactVideoTrend(response, context) {
    if (response.series.length > 1) {
        context.addIssue({
            code: "custom",
            message: "Exact video trend must contain at most one series",
            path: ["series"],
        });
    }
    if (response.total === null) {
        context.addIssue({
            code: "custom",
            message: "Exact video spend total must be numeric",
            path: ["total"],
        });
    }
    if (response.series.length === 0
        && (response.points.length > 0 || response.total !== 0)) {
        context.addIssue({
            code: "custom",
            message: "An absent exact video must have no points and a zero total",
        });
    }
}
export const exactVideoTrendQueryRowSchema = trendQueryRowSchema
    .superRefine(validateExactVideoTrend);
export const reportingCoverageSchema = z
    .object({
    /** @deprecated Retained as null for one wire-compatibility window. */
    creativeResolutionCompleteFrom: publicReportDateSchema.nullable(),
    creativeResolutionThreshold: coverageRate,
    videoMetricThreshold: coverageRate,
})
    .strict();
export const reportingEnvelopeShape = {
    attributionMaturityDays: z
        .number()
        .int()
        .min(0)
        .max(MAXIMUM_ATTRIBUTION_MATURITY_DAYS),
    cacheStatus: z.enum(["stale", "uncached", "versioned"]),
    snapshotIdentity: z
        .string()
        .regex(/^[0-9a-f]{64}$/)
        .nullable(),
    completeness: z
        .object({
        latestCompleteDay: publicReportDateSchema.nullable(),
        latestObservedDay: publicReportDateSchema.nullable(),
        partialLatestDayExcluded: z.boolean(),
    })
        .strict(),
    coverage: reportingCoverageSchema,
    effectiveRange: z
        .object({
        from: publicReportDateSchema,
        isEmpty: z.boolean(),
        to: publicReportDateSchema,
    })
        .strict(),
    freshness: z
        .object({
        attributionWindow: z.string().min(1).max(256),
        lastSyncAt: z.iso.datetime({ offset: true }).nullable(),
        latestObservedDate: publicReportDateSchema.nullable(),
        sourceStatus: z.enum(["complete", "missing"]),
    })
        .strict(),
    maturityCutoff: publicReportDateSchema,
};
export const videoReportResponseSchema = reportQueryRowSchema
    .extend(reportingEnvelopeShape)
    .strict();
export const drilldownResponseSchema = drilldownQueryRowSchema
    .extend(reportingEnvelopeShape)
    .strict();
export const comparisonResponseSchema = comparisonQueryRowSchema
    .extend(reportingEnvelopeShape)
    .strict()
    .superRefine((response, context) => {
    const rowKeys = response.rows.map(({ brandId, videoId }) => `${brandId}\0${videoId}`);
    if (new Set(rowKeys).size !== rowKeys.length) {
        context.addIssue({
            code: "custom",
            message: "Comparison rows must be unique by brand and video",
            path: ["rows"],
        });
    }
});
export const exactVideoDetailResponseSchema = exactVideoDetailQueryRowSchema
    .extend(reportingEnvelopeShape)
    .strict();
export const trendResponseSchema = trendQueryRowSchema
    .extend(reportingEnvelopeShape)
    .strict()
    .superRefine((response, context) => {
    const seriesKeys = response.series.map(({ brandId, videoId }) => `${brandId}\0${videoId}`);
    if (new Set(seriesKeys).size !== seriesKeys.length) {
        context.addIssue({ code: "custom", message: "Trend series must be unique" });
    }
    const series = new Set(seriesKeys);
    const breakout = response.series.length > 0;
    if (response.points.some(({ brandId }) => (brandId !== null) !== breakout)) {
        context.addIssue({
            code: "custom",
            message: "Trend points must use one aggregate or breakout identity mode",
        });
    }
    const pointKeys = response.points.map(({ brandId, day, videoId }) => `${brandId ?? ""}\0${videoId ?? ""}\0${day}`);
    if (new Set(pointKeys).size !== pointKeys.length) {
        context.addIssue({
            code: "custom",
            message: "Trend points must be unique per series and day",
        });
    }
    const pointSeries = new Set(response.points.flatMap(({ brandId, videoId }) => brandId === null ? [] : [`${brandId}\0${videoId}`]));
    if (response.points.some(({ brandId, videoId }) => brandId !== null && !series.has(`${brandId}\0${videoId}`))) {
        context.addIssue({
            code: "custom",
            message: "Trend point has no series identity",
        });
    }
    if (series.size !== pointSeries.size || [...series].some((key) => !pointSeries.has(key))) {
        context.addIssue({
            code: "custom",
            message: "Trend series and point identities must match exactly",
        });
    }
    const resolutionDays = response.resolution.map(({ day }) => day);
    if (new Set(resolutionDays).size !== resolutionDays.length) {
        context.addIssue({
            code: "custom",
            message: "Trend resolution days must be unique",
        });
    }
    const resolutionByDay = new Map(response.resolution.map((entry) => [entry.day, entry]));
    for (const point of response.points) {
        if (point.day < response.effectiveRange.from ||
            point.day > response.effectiveRange.to) {
            context.addIssue({
                code: "custom",
                message: "Trend point is outside the effective range",
            });
        }
        if (point.maturing !== (point.day > response.maturityCutoff)) {
            context.addIssue({
                code: "custom",
                message: "Trend maturity flag disagrees with the cutoff",
            });
        }
        if (point.value !== null &&
            !resolutionByDay.has(point.day)) {
            context.addIssue({
                code: "custom",
                message: "Populated trend day has no resolution metadata",
            });
        }
    }
    for (const entry of response.resolution) {
        if (entry.day < response.effectiveRange.from ||
            entry.day > response.effectiveRange.to) {
            context.addIssue({
                code: "custom",
                message: "Trend resolution is outside the effective range",
            });
        }
        const expected = entry.rate === null ||
            entry.rate < response.coverage.creativeResolutionThreshold;
        if (entry.belowThreshold !== expected) {
            context.addIssue({
                code: "custom",
                message: "Trend resolution flag disagrees with its threshold",
            });
        }
    }
});
export const exactVideoTrendResponseSchema = trendResponseSchema
    .superRefine(validateExactVideoTrend);
export const reportingErrorCodeSchema = z.enum([
    "REPORTING_BUDGET_EXCEEDED",
    "REPORTING_TIMEOUT",
    "REPORTING_UNAVAILABLE",
    "REPORTING_QUOTA_EXCEEDED",
    "REPORTING_REQUEST_INVALID",
]);
export const reportingErrorResponseSchema = z.discriminatedUnion("code", [
    z
        .object({
        code: z.literal("REPORTING_REQUEST_INVALID"),
        message: z.literal("Invalid reporting request"),
        statusCode: z.literal(400),
    })
        .strict(),
    z
        .object({
        code: z.literal("REPORTING_QUOTA_EXCEEDED"),
        message: z.literal("Reporting query quota exceeded"),
        statusCode: z.literal(429),
    })
        .strict(),
    z
        .object({
        code: z.literal("REPORTING_BUDGET_EXCEEDED"),
        message: z.literal("Reporting query exceeded its data budget"),
        statusCode: z.literal(503),
    })
        .strict(),
    z
        .object({
        code: z.literal("REPORTING_TIMEOUT"),
        message: z.literal("Reporting query timed out"),
        statusCode: z.literal(504),
    })
        .strict(),
    z
        .object({
        code: z.literal("REPORTING_UNAVAILABLE"),
        message: z.literal("Reporting data is temporarily unavailable"),
        statusCode: z.literal(503),
    })
        .strict(),
]);
//# sourceMappingURL=reporting.js.map